import React from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { Alert, Snackbar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { kebabCase } from "change-case";

import { RootState } from "../../app/store";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { toggleSharePrompt } from "../../app/promptsSlice";
import ShareLink from "./ShareLink";
import NotSharable from "./NotSharable";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

function ShareModal({ open, onClose }: { open: boolean; onClose: any }) {
  const dispatch = useAppDispatch();

  const selectedPrompt = useAppSelector(
    (state: RootState) => state.prompts.selectedPrompt
  );

  const isTogglingShare = useAppSelector(
    (state: RootState) => state.prompts.isTogglingShare
  );

  const [errorMessage, setErrorMessage] = React.useState("");
  const [openSnackbar, setOpenSnackbar] = React.useState(false);

  const handleCloseSnackbar = () => {
    setErrorMessage("");
    setOpenSnackbar(false);
  };

  const handleChangeShare = () => {
    dispatch(toggleSharePrompt(selectedPrompt.id));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disablePortal
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={openSnackbar && errorMessage !== ""}
          autoHideDuration={1000}
          onClose={handleCloseSnackbar}
          message=""
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
        <Box
          sx={{
            bgcolor: "background.paper",
            border: "2px solid #000",
            borderRadius: 2,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              pl: 2,
              pt: 1,
              pb: 1,
              borderBottom: "#000 1px solid",
              display: "flex",
            }}
          >
            <Box
              key="model-title"
              sx={{ display: "flex", flexGrow: 1, alignItems: "center" }}
            >
              <Typography variant="button">Share Prompt</Typography>
            </Box>
            <Box sx={{ mr: 1 }}>
              <IconButton color="inherit" edge="start" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              p: 1,
            }}
          >
            <Box
              sx={{
                alignContent: "center",
                alignItems: "center",
                display: "flex",
                grow: 1,
              }}
            >
              {isTogglingShare ? (
                <CircularProgress sx={{ m: 2, mt: 1, mb: 1 }} size={26} />
              ) : (
                <Switch
                  checked={selectedPrompt.isShared}
                  inputProps={{ "aria-label": "Enable Share" }}
                  onChange={handleChangeShare}
                />
              )}
            </Box>
            <Box>
              {selectedPrompt.isShared && (
                <ShareLink
                  link={`https://shared.myuserprompt.com/prompts/${
                    selectedPrompt.sharedPromptId
                  }/${kebabCase(selectedPrompt.title || "")}.html`}
                />
              )}
              {!selectedPrompt.isShared && <NotSharable />}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default ShareModal;
